import React, { useState } from 'react';
import { LogIn, LogOut, Settings, Loader2, Share2, Users, HelpCircle, ChevronDown, AlertTriangle, Wrench, Keyboard, Rocket, TrendingUp, Gift } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { AdminMenuLink } from './HealthPulse';
import ReferProviderModal from './ReferProviderModal';

const HeaderUserMenu = ({
  user,
  setShowAuthModal,
  setShowProfileModal,
  setShowShareModal,
  setShowTeamSettings,
  setShowProductivityReport,
  logout,
  isSharing,
  shareCode,
  historyShareCode,
  userRole,
  isAdmin,
  includePatientQuotes,
  setIncludePatientQuotes,
  onShowShortcuts,
  onShowTutorial,
  onShowBugReport,
  onShowShipVersion,
  navigate,
}) => {
  // Mar 3, 2026 — Refer-a-Provider modal state (menu item + separate header chip).
  const [showReferModal, setShowReferModal] = useState(false);
  if (user) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm"
            className="h-8 px-2 gap-1.5 hover:bg-primary/10 rounded-lg border border-border/50 hover:border-primary/30 transition-all"
            data-testid="user-menu-btn">
            {user.picture ? (
              <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
            ) : (
              <div className="w-5 h-5 rounded-full bg-primary/30 flex items-center justify-center">
                <span className="text-[10px] font-bold text-primary">{(user.name || user.username)?.charAt(0).toUpperCase()}</span>
              </div>
            )}
            <span className="text-xs hidden lg:inline max-w-[160px] truncate">{user.name || user.username || 'Guest'}</span>
            <ChevronDown className="w-3 h-3 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" side="bottom" className="w-48 p-1.5" data-testid="user-menu-dropdown" sideOffset={8}>
          <div className="px-2 py-2 mb-1 border-b border-border">
            <p className="text-sm font-medium text-popover-foreground truncate">{user.name || user.username}</p>
            <p className="text-[11px] text-muted-foreground truncate">{user.email || 'Guest account'}</p>
          </div>
          <button onClick={() => setShowProfileModal(true)} className="w-full flex items-center gap-2.5 px-2 py-2 text-sm text-popover-foreground hover:bg-secondary rounded-md transition-colors" data-testid="menu-settings">
            <Settings className="w-4 h-4 text-muted-foreground" /> Settings
          </button>
          <button onClick={() => setShowReferModal(true)} className="w-full flex items-center gap-2.5 px-2 py-2 text-sm text-amber-200 hover:bg-amber-500/10 rounded-md transition-colors" data-testid="menu-refer-provider">
            <Gift className="w-4 h-4 text-amber-400" /> Refer a Provider
          </button>
          <button onClick={() => setShowShareModal(true)} disabled={isSharing}
            className={`w-full flex items-center gap-2.5 px-2 py-2 text-sm rounded-md transition-colors ${
              shareCode ? 'text-green-400 hover:bg-green-500/10' : 
              historyShareCode ? 'text-blue-400 hover:bg-blue-500/10' : 
              'text-popover-foreground hover:bg-secondary'
            }`} data-testid="menu-share">
            {isSharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4 text-muted-foreground" />}
            {shareCode ? 'Share (Live)' : historyShareCode ? 'Share (Active)' : 'Share'}
          </button>
          {userRole === 'provider' && (
            <button onClick={() => setShowTeamSettings && setShowTeamSettings(true)} className="w-full flex items-center gap-2.5 px-2 py-2 text-sm text-popover-foreground hover:bg-secondary rounded-md transition-colors" data-testid="menu-team-manage">
              <Users className="w-4 h-4 text-green-400" /> Manage Team
            </button>
          )}
          {userRole === 'provider' && (
            <button
              onClick={() => setShowProductivityReport && setShowProductivityReport(true)}
              className="w-full flex items-center gap-2.5 px-2 py-2 text-sm text-popover-foreground hover:bg-secondary rounded-md transition-colors"
              data-testid="menu-productivity-report"
              title="Printable OER-ready productivity report (any date range, PDF / Print / CSV)"
            >
              <TrendingUp className="w-4 h-4 text-amber-400" /> Productivity Report
            </button>
          )}
          {isAdmin && <AdminMenuLink navigate={navigate} />}
          {isAdmin && (
            <button
              onClick={() => onShowShipVersion && onShowShipVersion()}
              className="w-full flex items-center gap-2.5 px-2 py-2 text-sm text-amber-400 hover:bg-amber-500/10 rounded-md transition-colors"
              data-testid="menu-ship-version"
            >
              <Rocket className="w-4 h-4" /> Ship Version
            </button>
          )}
          <div className="border-t border-border mt-1 pt-1 px-2 py-1.5">
            <span className="text-[11px] text-muted-foreground font-medium">Patient's Own Words</span>
            <div className="flex gap-1 mt-1">
              {[
                { value: 'none', label: 'None' },
                { value: 'summary', label: 'Summary' },
                { value: 'quotes', label: 'Quotes' },
              ].map(opt => (
                <button key={opt.value} onClick={() => setIncludePatientQuotes && setIncludePatientQuotes(opt.value)}
                  className={`flex-1 h-6 rounded text-[10px] font-medium transition-all ${
                    includePatientQuotes === opt.value ? 'bg-primary text-primary-foreground' : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'
                  }`} data-testid={`menu-quotes-${opt.value}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="border-t border-border mt-1 pt-1">
            <button onClick={() => onShowShortcuts && onShowShortcuts()} className="w-full flex items-center gap-2.5 px-2 py-1.5 text-sm text-muted-foreground hover:bg-secondary rounded-md transition-colors" data-testid="menu-shortcuts">
              <Keyboard className="w-4 h-4" /> Keyboard Shortcuts
            </button>
            <button onClick={() => onShowTutorial && onShowTutorial()} className="w-full flex items-center gap-2.5 px-2 py-1.5 text-sm text-muted-foreground hover:bg-secondary rounded-md transition-colors" data-testid="menu-help">
              <HelpCircle className="w-4 h-4" /> How to Use
            </button>
            <button onClick={() => onShowBugReport && onShowBugReport()} className="w-full flex items-center gap-2.5 px-2 py-1.5 text-sm text-amber-400 hover:bg-amber-500/10 rounded-md transition-colors" data-testid="menu-report-bug">
              <Wrench className="w-4 h-4" /> Report an Issue
            </button>
            <button onClick={() => { if (window.confirm('Clear local settings on this device?\nServer history stays safe.')) { localStorage.clear(); sessionStorage.clear(); window.location.href = window.location.origin; } }} className="w-full flex items-center gap-2.5 px-2 py-1.5 text-sm text-amber-400 hover:bg-amber-500/10 rounded-md transition-colors" data-testid="menu-reset">
              <AlertTriangle className="w-4 h-4" /> Reset Local
            </button>
          </div>
          <div className="border-t border-border mt-1 pt-1">
            <button onClick={logout} className="w-full flex items-center gap-2.5 px-2 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-md transition-colors font-medium" data-testid="menu-logout">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </PopoverContent>
        <ReferProviderModal open={showReferModal} onOpenChange={setShowReferModal} />
      </Popover>
    );
  }

  return (
    <>
      <Button variant="default" size="sm" onClick={() => setShowAuthModal(true)}
        className="h-8 text-xs px-3 bg-primary hover:bg-primary/90 rounded-lg font-semibold"
        data-testid="sign-in-btn">
        <LogIn className="w-3.5 h-3.5 mr-1.5" /> Sign In
      </Button>
      <Popover>
        <PopoverTrigger asChild>
          <button className="inline-flex items-center justify-center h-7 w-7 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors" data-testid="help-btn">
            <HelpCircle className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" side="bottom" className="w-44 p-1.5" sideOffset={8}>
          <button onClick={() => onShowShortcuts && onShowShortcuts()} className="w-full flex items-center gap-2.5 px-2 py-1.5 text-sm text-popover-foreground hover:bg-secondary rounded-md transition-colors" data-testid="help-shortcuts">
            <Keyboard className="w-4 h-4 text-muted-foreground" /> Shortcuts
          </button>
          <button onClick={() => onShowTutorial && onShowTutorial()} className="w-full flex items-center gap-2.5 px-2 py-1.5 text-sm text-popover-foreground hover:bg-secondary rounded-md transition-colors" data-testid="help-tutorial">
            <HelpCircle className="w-4 h-4 text-muted-foreground" /> How to Use
          </button>
          <button onClick={() => onShowBugReport && onShowBugReport()} className="w-full flex items-center gap-2.5 px-2 py-1.5 text-sm text-amber-400 hover:bg-amber-500/10 rounded-md transition-colors" data-testid="help-report-bug">
            <Wrench className="w-4 h-4" /> Report Issue
          </button>
        </PopoverContent>
      </Popover>
    </>
  );
};

export default HeaderUserMenu;
